import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldX } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { adminGetTaxProfile, type AdminTaxView } from "@/lib/affiliateTax";
import { maskReferredUserId } from "@/lib/affiliateDashboard";

type SubmittedRow = {
  user_id: string;
  form_type: string;
  legal_name: string;
  country: string;
  submitted_at: string;
};

export function TaxReviewTab() {
  const { isSuperAdmin } = useUserRole();
  const [rows, setRows] = useState<SubmittedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [view, setView] = useState<AdminTaxView | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("affiliate_tax_profiles")
      .select("user_id, form_type, legal_name, country, submitted_at")
      .eq("status", "submitted")
      .order("submitted_at", { ascending: true });
    setLoading(false);
    if (error) return toast.error(error.message);
    setRows((data ?? []) as SubmittedRow[]);
  };
  useEffect(() => { void load(); }, []);

  const openDrawer = async (userId: string) => {
    setOpenUserId(userId);
    setReason("");
    setView(null);
    setViewLoading(true);
    try { setView(await adminGetTaxProfile(userId)); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed to load"); }
    finally { setViewLoading(false); }
  };

  const act = async (action: "verify" | "reject") => {
    if (!openUserId) return;
    if (action === "reject" && reason.trim().length === 0) {
      return toast.error("Reason is required to reject");
    }
    setActing(true);
    const { error } = await supabase.rpc("admin_review_tax_profile", {
      _user_id: openUserId, _action: action, _reason: action === "reject" ? reason.trim() : null,
    });
    setActing(false);
    if (error) return toast.error(error.message);
    toast.success(action === "verify" ? "Tax form verified" : "Tax form rejected");
    setOpenUserId(null);
    await load();
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Tax forms — awaiting review</CardTitle>
        <p className="text-sm text-muted-foreground">
          W-9 / W-8BEN submissions from affiliates. Verify before creating payout batches.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Submitted</TableHead>
                <TableHead>Affiliate</TableHead>
                <TableHead>Form</TableHead>
                <TableHead>Legal name</TableHead>
                <TableHead>Country</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">
                  Nothing awaiting review.
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.user_id}>
                  <TableCell className="whitespace-nowrap text-xs">
                    {new Date(r.submitted_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{maskReferredUserId(r.user_id)}</TableCell>
                  <TableCell><Badge variant="outline">{r.form_type.toUpperCase()}</Badge></TableCell>
                  <TableCell>{r.legal_name}</TableCell>
                  <TableCell>{r.country}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openDrawer(r.user_id)}
                      disabled={!isSuperAdmin}>
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={!!openUserId} onOpenChange={(o) => !o && setOpenUserId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review tax form</DialogTitle>
            <DialogDescription>
              Sensitive identifiers are decrypted only for this review session.
            </DialogDescription>
          </DialogHeader>
          {viewLoading || !view ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Form type" value={view.profile.form_type.toUpperCase()} />
                <Field label="Country" value={view.profile.country} />
                <Field label="Legal name" value={view.profile.legal_name} />
                <Field label="Business name" value={view.profile.business_name ?? "—"} />
                {view.profile.tax_classification ? (
                  <Field label="Tax classification" value={view.profile.tax_classification} />
                ) : null}
                {view.profile.treaty_country ? (
                  <Field label="Treaty country" value={view.profile.treaty_country} />
                ) : null}
                <Field label={`TIN (${view.profile.tin_kind ?? "—"})`} value={view.tin ?? "—"} />
                {view.foreign_tin ? (
                  <Field label="Foreign TIN" value={view.foreign_tin} />
                ) : null}
              </div>
              {view.address ? (
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Address</div>
                  <div className="rounded-md border border-border bg-muted/30 p-2 text-sm">
                    {view.address.line1}<br />
                    {view.address.line2 ? <>{view.address.line2}<br /></> : null}
                    {view.address.city}{view.address.region ? `, ${view.address.region}` : ""}
                    {view.address.postal ? ` ${view.address.postal}` : ""}<br />
                    {view.address.country}
                  </div>
                </div>
              ) : null}
              <div className="rounded-md bg-muted/30 p-2 text-xs text-muted-foreground">
                Signed as <strong>{view.profile.signed_name}</strong> on{" "}
                {view.profile.signed_at ? new Date(view.profile.signed_at).toLocaleString() : "—"}
                {view.profile.signed_ip ? ` from ${view.profile.signed_ip}` : ""}.
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Rejection reason (required to reject)</label>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. TIN appears mistyped — please correct and resubmit." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUserId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => act("reject")}
              disabled={acting || viewLoading || !reason.trim()}>
              {acting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ShieldX className="mr-1 h-4 w-4" />}
              Reject
            </Button>
            <Button onClick={() => act("verify")} disabled={acting || viewLoading}>
              {acting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1 h-4 w-4" />}
              Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  );
}
